import React, { useEffect, useState } from 'react';
import { DateRangePicker } from '../dashboard/DateRangePicker';
import { GranularitySelector, Granularity } from '../dashboard/GranularitySelector';
import { AuthenticationFilters } from './AuthenticationFilters';
import { EmptyState } from './EmptyState';
interface DateRange {
  start: Date;
  end: Date;
}
interface AuthenticationTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function AuthenticationTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: AuthenticationTabProps) {
  // Filter states - no successRateMetric for Authentication tab
  const [deduplication, setDeduplication] = useState<'deduplicated' | 'raw'>('deduplicated');
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDeduplication('deduplicated');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setInteractionTypes([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-656">
      {/* Main Filters Row */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-657">
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-658" />
        <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-659" />
        <AuthenticationFilters deduplication={deduplication} onDeduplicationChange={setDeduplication} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-660" />
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-661">
        <div className="p-6 border-b border-gray-200" data-id="element-662">
          <div className="flex items-center justify-between" data-id="element-663">
            <div className="flex items-center gap-4" data-id="element-664">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-665">
                Key metrics
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-666">
                <span data-id="element-667">Compared to</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-668">
                  Previous period
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" data-id="element-669">
                    <path d="M4.427 9.573l3.396-3.396a.25.25 0 01.354 0l3.396 3.396a.25.25 0 01-.177.427H4.604a.25.25 0 01-.177-.427z" data-id="element-670" />
                  </svg>
                </button>
                <span className="text-gray-500" data-id="element-671">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-672">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-674">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-673" />
        </div>
      </div>

      {/* 3D Secure authentication outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-675">
        <div className="p-6 border-b border-gray-200" data-id="element-676">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-677">
            3D Secure authentication outcomes
          </h2>
        </div>

        <div className="p-12" data-id="element-678">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-680">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-679" />
        </div>
      </div>

      {/* 3D Secure authentication request reasons */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-681">
        <div className="p-6 border-b border-gray-200" data-id="element-682">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-683">
            3D Secure authentication request reasons
          </h2>
        </div>

        <div className="p-12" data-id="element-684">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-686">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-685" />
        </div>
      </div>
    </div>;
}